import { Form, FormField } from "@babackapp/ui/components/form";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right.ts";
import { SettingsFormPageButtonBar } from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  AiSuggestionsFormSchema,
  AiSuggestionsFormValues,
} from "./ai-suggestions-card";
import useTranslation from "@babackapp/translations/use-translation";

type AiSuggestionsFormProps = {
  defaultValues?: Partial<AiSuggestionsFormValues>;
  onSubmit: (data: AiSuggestionsFormValues) => void;
};

export default function AiSuggestionsForm({
  onSubmit,
  defaultValues,
}: AiSuggestionsFormProps) {
  const t = useTranslation();
  const hasAdminRight = useViewerHasAtLeastAdminRights();

  const disabled = !hasAdminRight
    ? "You don't have permission to edit this"
    : false;

  const form = useForm<AiSuggestionsFormValues>({
    resolver: zodResolver(AiSuggestionsFormSchema),
    defaultValues,
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col justify-start gap-8 mt-8"
      >
        <p className="text-sm text-muted-foreground">
          {t("settings.customerPortal.aiSuggestions.intro")}
        </p>

        <FormField
          control={form.control}
          name="shouldShowSizeSuggestions"
          render={({ field }) => (
            <FormSwitch
              label={t("settings.exchange.fields.showSizeSuggestions.title")}
              description={t(
                "settings.exchange.fields.showSizeSuggestions.description",
              )}
              {...field}
              // Absent or null = on (the card predates the setting); the form
              // keeps null until the merchant flips the switch.
              value={field.value ?? true}
              disabled={disabled}
            />
          )}
        />

        <FormField
          control={form.control}
          name="shouldShowProductRecommendations"
          render={({ field }) => (
            <FormSwitch
              label={t(
                "settings.customerPortal.aiSuggestions.productRecommendations.title",
              )}
              description={t(
                "settings.customerPortal.aiSuggestions.productRecommendations.description",
              )}
              {...field}
              // Same as the size switch: absent or null = on.
              value={field.value ?? true}
              disabled={disabled}
            />
          )}
        />

        <SettingsFormPageButtonBar />
      </form>
    </Form>
  );
}
